import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect
} from 'react';
import { useTranslations } from 'next-intl';
import {
  CatButton,
  CatCard,
  CatIconShop,
  CatHeading,
  CatIconClose
} from 'components/blocks-components';
import { Conditional } from 'components/pcc-components';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import GetGroupPartType from 'components/group-parts-type/group-parts-type';
import { LineItem } from 'types/parts-diagram';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import { revalidateCart } from 'services/parts-diagram/actions';
import FullMatchFitment from 'app/[lang]/[esite]/(core)/product/[product]/_components/full-match-fitment';
import { Image } from 'components/image';
import styles from '../parts-overview.module.scss';
import cx from 'classnames';
import PartCardPrice from '../part-card-price';
import PartsAvailabilityWrapper from 'components/parts-availability-wrapper';
import { PRODUCT_FALLBACK_IMAGE } from 'constants/common';
import { useDealerLocatorDrawer } from 'components/dealer-locator/dealer-locator-drawer-context';
import { useCoreContext } from 'providers/core-provider';
import ViewDiagramButton from 'app/[lang]/[esite]/(core)/parts-diagram/_components/parts-layout/system-components-wrapper/system-and-components-mobile/components/view-diagram-button';
import {
  GA_CHECK_PRICE,
  GA_PDP_DEFAULT_DIAGRAM,
  GA_PDP_LIST_DIAGRAM
} from 'constants/analyticsConstants';

import { CartResponse } from 'services/cart/types';
import { productPartsData } from 'types/product-diagram';
import {
  GA_ADD_TO_CART,
  GA_PDP_DEFAULT_VIEW_SECTION,
  GA_PDP_LIST_VIEW_SECTION,
  GA_PRIMARY_CTA
} from 'constants/analyticsConstants';
export interface PartsOverviewMobileCardProps {
  partItem: LineItem;
  selectedItem: string | null;
  onSelectItem: (referenceNumber: string | null) => void;
  handleOpen: (
    partNumber: string,
    price: ProductDealerPriceAvailability
  ) => Promise<void>;

  openQuickViewDrawer: Dispatch<SetStateAction<boolean>>;
  isSerialCompatible?: boolean;
  fromProductPage?: boolean;
  contactDealerBtn?: React.ReactNode;
  allCardsHasContactDealer?: boolean;
  isImageShown?: boolean;
  partsDiagramMobileView?: boolean;
  isPccPdpHeaderUpdateMobile?: boolean;
  currencyNotation?: string;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  showCloseIcon?: boolean;
  isCatCorp?: boolean;
  selectedEquipment?: Equipment;
  isActiveListTab?: boolean;
  isCardOverlay?: boolean;
  shouldShowPartsDiagramV2?: boolean;

  handleViewDiagram?: (lineItem: LineItem) => void;
  handleDisambiguationClick?: (partNumber: string) => void;
  shouldHideBtn?: boolean;
  cartDetails?: CartResponse;
  productData?: productPartsData;
}

const PartsOverviewMobileCard: FC<PartsOverviewMobileCardProps> = ({
  partItem,
  selectedItem,
  onSelectItem,
  handleOpen,
  openQuickViewDrawer,
  isSerialCompatible,
  fromProductPage,
  contactDealerBtn,
  allCardsHasContactDealer,
  isImageShown = false,
  partsDiagramMobileView = false,
  isPccPdpHeaderUpdateMobile = false,
  currencyNotation = '',
  isRTL = false,
  currencyLdFeatureFlag = false,
  showCloseIcon,
  isCatCorp,
  selectedEquipment,
  isActiveListTab = false,
  isCardOverlay = false,

  shouldShowPartsDiagramV2,
  handleViewDiagram,
  handleDisambiguationClick,
  shouldHideBtn = false,
  cartDetails,
  productData
}) => {
  const t = useTranslations();
  const { broadcastCartUpdate } = useCoreContext();
  const {
    partNumber = '',
    name = '',
    referenceNumber = '',
    ieSystemControlNumber = null,
    disambiguation = false,
    priceAndAvailabilityData,
    quantity,
    imageIdentifiers
  } = partItem || {};

  const {
    setIsCatCorp,
    handleAddToCart,
    disableAddToCart,
    analyticsCommonInfo: { dealerData, model } = {}
  } = usePartDiagram();

  useEffect(() => {
    setIsCatCorp(isCatCorp);
  }, [isCatCorp, setIsCatCorp]);

  const isArrangements = !!ieSystemControlNumber && !disambiguation;

  const { openDrawer } = useDealerLocatorDrawer();

  const handleAddItemToCart = useCallback(() => {
    const analyticsInformation = {
      currency: cartDetails?.orderItemPriceAvailabilityTotals?.currency,
      model,
      dealerData,
      manufacturer: productData?.manufacturer || 'cat',
      EventListInfo: isActiveListTab
        ? GA_PDP_LIST_DIAGRAM
        : GA_PDP_DEFAULT_DIAGRAM
    };
    handleAddToCart(partItem, quantity, analyticsInformation);
    revalidateCart();
    broadcastCartUpdate();
  }, [
    handleAddToCart,
    partItem,
    quantity,
    broadcastCartUpdate,
    model,
    dealerData,
    isActiveListTab,
    cartDetails?.orderItemPriceAvailabilityTotals?.currency,
    productData?.manufacturer
  ]);

  const replacementParts = priceAndAvailabilityData?.replacementParts;
  const hasReplacementParts = Array.isArray(replacementParts)
    ? replacementParts.length > 0
    : false;
  const isReplacedPart =
    !!priceAndAvailabilityData?.indirectReplacement || hasReplacementParts;

  const iscurrentItemSelected =
    selectedItem &&
    partItem?.referenceNumber &&
    selectedItem === partItem?.referenceNumber;
  const showAddtoCartButton =
    !isCatCorp && !isReplacedPart && priceAndAvailabilityData?.enableAddToCart;

  const showViewDiagramButton =
    (!partItem?.ieSystemControlNumber && disambiguation && !shouldHideBtn) ||
    (isArrangements && !shouldHideBtn);

  const shouldShowForSingleCard =
    !isCatCorp &&
    !priceAndAvailabilityData?.enableAddToCart &&
    !allCardsHasContactDealer &&
    !disambiguation &&
    !partItem?.ieSystemControlNumber;

  const shouldShowForAllCards = allCardsHasContactDealer && !isActiveListTab;

  const showContactDealerbtn =
    (shouldShowForSingleCard || shouldShowForAllCards) &&
    !showViewDiagramButton;

  const showPartAvailabilityLabel =
    !isArrangements && !isCatCorp && priceAndAvailabilityData?.enableAddToCart;

  const trackingContext = isActiveListTab
    ? GA_PDP_LIST_VIEW_SECTION
    : GA_PDP_DEFAULT_VIEW_SECTION;

  const handleOnClick = () => {
    if (!iscurrentItemSelected && partItem.referenceNumber) {
      return onSelectItem(partItem.referenceNumber);
    }
    onSelectItem(null);
  };

  const handleOnClose = () => {
    onSelectItem(null);
  };

  return (
    <Conditional test={!!name} fallback={null}>
      <CatCard
        className={cx({
          [styles['parts-overview-mobile-card']]: !iscurrentItemSelected,
          [styles['parts-overview-mobile-card--selected']]:
            iscurrentItemSelected
        })}
        data-testid="part-mobile-card"
      >
        <div className="d-flex flex-column gap-3">
          <div
            className={cx(
              'd-flex flex-row',
              styles['parts-overview-mobile-card__content']
            )}
          >
            <Conditional test={!!referenceNumber} fallback={null}>
              <CatButton
                key={partItem.referenceNumber}
                onClick={handleOnClick}
                variant="ghost"
                className={cx(styles['reference-number__mobile-button'])}
                data-testid="part-mobile-card-button"
                size="sm"
              >
                <CatHeading
                  variant="body-sm"
                  className={cx({
                    [styles['reference-number__mobile']]:
                      !iscurrentItemSelected,
                    [styles['reference-number__mobile--selected']]:
                      iscurrentItemSelected
                  })}
                >
                  {referenceNumber}
                </CatHeading>
              </CatButton>
            </Conditional>
            <div className="d-flex align-items-center">
              {!isImageShown && (
                <Image
                  className={styles['parts-overview-mobile-card__image']}
                  src={imageIdentifiers || PRODUCT_FALLBACK_IMAGE}
                  alt={`${partNumber}: ${name}`}
                  width={80}
                  height={80}
                />
              )}
              <div className="d-flex flex-column">
                <GetGroupPartType
                  partNumber={partNumber}
                  name={name}
                  disambiguation={disambiguation}
                  ieSystemControlNumber={ieSystemControlNumber}
                  price={priceAndAvailabilityData}
                  handleOpen={handleOpen}
                  setDrawerActive={openQuickViewDrawer}
                  catHeadingClassName={styles['partsReference__text']}
                  catLinkSize="sm"
                  catTextCapitalize={true}
                  isActiveListTab={isActiveListTab}
                  fromProductPage={fromProductPage}
                  lineItem={partItem}
                  cartDetails={cartDetails}
                  productData={productData}
                />
                <PartCardPrice
                  currency={currencyNotation}
                  partItem={partItem}
                  className="m-0 p-0"
                  modifiedPriceData={priceAndAvailabilityData}
                  isMobileView={true}
                  fromProductPage={fromProductPage}
                  isRTL={isRTL}
                  currencyLdFeatureFlag={currencyLdFeatureFlag}
                  isCatCorp={isCatCorp}
                />
                <Conditional test={showPartAvailabilityLabel}>
                  <PartsAvailabilityWrapper
                    availabilityData={priceAndAvailabilityData}
                    shouldDisplayLabel={false}
                    isLoading={false}
                    isPccPdpHeaderUpdateMobile={isPccPdpHeaderUpdateMobile}
                    partsDiagramMobileView={partsDiagramMobileView}
                    openInDrawer={partsDiagramMobileView}
                    showAvailabilityAsBottomDrawer={true}
                  ></PartsAvailabilityWrapper>
                </Conditional>
              </div>
            </div>
            <Conditional test={showCloseIcon}>
              <CatIconClose
                className="text-end ms-auto mt-1"
                onClick={handleOnClose}
              />
            </Conditional>
          </div>
          <div className="d-flex justify-content-between align-items-end">
            <div className="d-flex flex-column gap-1">
              <Conditional test={showAddtoCartButton}>
                <p className="cat-u-theme-typography-footnote m-0">
                  {t('SSRO_RECOMMENDEDQTY')} {quantity}
                </p>
              </Conditional>
              <Conditional test={isSerialCompatible}>
                <FullMatchFitment
                  className={styles['parts-overview-mobile-card__fitment-text']}
                  selectedEquipment={selectedEquipment}
                  shouldShowPartsDiagramV2={shouldShowPartsDiagramV2}
                />
              </Conditional>
            </div>
            {!disambiguation && (
              <Conditional
                test={!!ieSystemControlNumber}
                fallback={
                  showAddtoCartButton && (
                    <CatButton
                      disabled={disableAddToCart}
                      onClick={handleAddItemToCart}
                      data-track-event={GA_PRIMARY_CTA}
                      data-track-attr-nltext={GA_ADD_TO_CART}
                      data-track-attr-context={trackingContext}
                    >
                      {t('ADD')}
                      <CatIconShop slot="after" />
                    </CatButton>
                  )
                }
              />
            )}
            {!isActiveListTab && isCardOverlay && isCatCorp && (
              <CatButton
                size="md"
                onClick={openDrawer}
                data-track-event={GA_PRIMARY_CTA}
                data-track-attr-nltext={GA_CHECK_PRICE}
                data-track-attr-context={trackingContext}
              >
                {t('CAT_CHECK_PRICE')}
              </CatButton>
            )}
            <Conditional test={showContactDealerbtn}>
              <div className="text-end">{contactDealerBtn}</div>
            </Conditional>
            <Conditional
              test={!ieSystemControlNumber && disambiguation && !shouldHideBtn}
            >
              <div className={styles['view-diagram-button-positioned']}>
                <ViewDiagramButton
                  data-testid="cat-button"
                  className={styles['view-diagram-button']}
                  onClick={() => {
                    if (handleDisambiguationClick) {
                      handleDisambiguationClick(partItem?.partNumber);
                    }
                  }}
                />
              </div>
            </Conditional>
            <Conditional test={isArrangements && !shouldHideBtn}>
              <div className={styles['view-diagram-button-positioned']}>
                <ViewDiagramButton
                  data-testid="cat-button"
                  className={styles['view-diagram-button']}
                  onClick={() => {
                    if (handleViewDiagram) {
                      handleViewDiagram(partItem);
                    }
                  }}
                />
              </div>
            </Conditional>
          </div>
        </div>
      </CatCard>
    </Conditional>
  );
};

export default React.memo(PartsOverviewMobileCard);
